import React, { useCallback, useEffect, useState, useMemo,useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useNavigate, useParams, Link } from 'react-router-dom';
import '../styles/Topics.css';
import Header from '../components/Header';
import RestartTopicStatusModal from '../components/RestartTopicStatusModal'
import TopicConfirmModal from '../components/TopicConfirmModal';
import ActiveTopicWarningModal from '../components/ActiveTopicWarningModal';
import LiveUsersModal from '../components/LiveUsersModal';
import SpeakingPanel from '../components/SpeakingPanel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'; 
import { faDesktop, faPenToSquare, faTrash, faArrowUp, faArrowDown, faPlus,faChevronLeft, faCirclePlay, faCircleStop, faRotateLeft, faUsers, faSquarePollVertical, faEllipsisV, faToggleOn, faToggleOff, faBookmark, faFilePen, faLandmarkFlag } from '@fortawesome/free-solid-svg-icons';
import Footer from '../components/Footer';
import useVoteWebSocket from "../hooks/useVoteWebSocket";
import usePresenterWebSocket from "../hooks/usePresenterWebSocket";
import useAmendmentPresenterWebSocket from "../hooks/useAmendmentPresenterWebSocket";
import useNewTopicWebSocket from "../hooks/useNewTopicWebSocket";
import { useTranslation } from "react-i18next";
import api from '../api/axios';
import { openPdfTab, sanitizeFilenameForUrl } from '../utils/pdfTab';
import { storeTermImages, isTermPopulated } from '../cache/imageCache';

function Topics() { 
    const [topics, setTopics] = useState([]);
    const [presentedTopicId, setPresentedTopicId] = useState(null);
    const [amendmentIsPresented, setAmendmentIsPresented] = useState(false);
    const [presentedAmendmentId, setPresentedAmendmentId] = useState(null);
    const { id } = useParams();
    const { municipalityId } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTopicId, setSelectedTopicId] = useState(null);
    const [selectedTopicTitle, setSelectedTopicTitle] = useState(null);
    const [openMenus, setOpenMenus] = useState({}); // Object to track open menus
    const menuRefs = useRef({});
    const [isLiveModalOpen, setIsLiveModalOpen] = useState(false);
    const [sessionTitle, setSessionTitle] = useState('');
    const { t } = useTranslation();
    const selectedLang = localStorage.getItem("selectedLanguage") || "mk";
    const [sessionMunicipalityTermId, setSessionMunicipalityTermId] = useState(null);
    const [showFixDiv, setshowFixDiv] = useState(false);
    const userInfo = useMemo(() => JSON.parse(localStorage.getItem('userInfo')), []);
    const token = localStorage.getItem('jwtToken');
    const navigate = useNavigate();
    const [currentVotes, setCurrentVotes] = useState({});
    const [onlineUsersNumber, setOnlineUsersNumber] = useState(0);
    const [isRestartModalOpen, setIsRestartModalOpen] = useState(false);
    const [restartTopicId, setRestartTopicId] = useState(null);
    const [restartTopicTitle, setRestartTopicTitle] = useState('');
    const [isActiveWarningModalOpen, setIsActiveWarningModalOpen] = useState(false);
    const [activeWarningTopicTitle, setActiveWarningTopicTitle] = useState('');
    const [activeWarningTopicId, setActiveWarningTopicId] = useState(null);
    const [pendingVotingTopicId, setPendingVotingTopicId] = useState(null);
    const [topicsLoaded, setTopicsLoaded] = useState(false);
    const [showNumber, setShowNumber] = useState(false);
    const [showFloatingBack, setShowFloatingBack] = useState(false);
    const [isVoteAction, setIsVoteAction] = useState(false);
    const isVoteActionRef = useRef(isVoteAction);
    const votingInProgressRef = useRef(new Set());
    // WEB SOCKETS
    const { messages: voteMessages, sendVote } = useVoteWebSocket(id);
    const { messages: presenterMessages, sendPresenterUpdate } = usePresenterWebSocket(id);
    const { messages: amendmentPresenterMessages } = useAmendmentPresenterWebSocket(id);
    const { messages: newTopicMessages, sendNewTopic } = useNewTopicWebSocket(id);

    useEffect(() => {

        if (userInfo.role === "ROLE_GUEST") return;

        if (!sessionMunicipalityTermId || !municipalityId) return;

        const mandates = JSON.parse(localStorage.getItem(`municipalityMandates_${municipalityId}`)) || [];

        if (mandates.length === 0) return;

        const newestMandateId = Math.max(...mandates.map(m => m.id));

        setshowFixDiv(sessionMunicipalityTermId === newestMandateId);

    }, [sessionMunicipalityTermId, municipalityId, userInfo.role]);

    const [isOn, setIsOn] = useState(() => {
        const saved = localStorage.getItem(`toggle_state_session_${id}`);
        return saved === 'true'; // convert to boolean
    });

    const handleToggle = () => {
        const newValue = !isOn;
        setIsOn(newValue);
        localStorage.setItem(`toggle_state_session_${id}`, newValue);
    };

   const openRestartModal = (topicId, topicTitle) => {
        setRestartTopicId(topicId);
        setRestartTopicTitle(topicTitle);
        setIsRestartModalOpen(true);
    };

    const closeRestartModal = () => {
        setIsRestartModalOpen(false);
        setRestartTopicId(null);
    };

    const handleRestartConfirm = () => {
    if (restartTopicId) {
        restartVoting(restartTopicId, token);
    }
    closeRestartModal();
    };

    const toggleMenu = (id) => {
        setOpenMenus((prevMenus) => ({
        ...prevMenus,
        [id]: !prevMenus[id], // Toggle the specific menu's visibility
        }));
    };

    const toggleVisibility = () => {
        setShowNumber(!showNumber); // Toggle the visibility
        fetchOnlineUsers();
    };

    const handleClickOutside = useCallback(
        (event) => {
            Object.keys(menuRefs.current).forEach((id) => {
            if (
                openMenus[id] &&
                menuRefs.current[id] &&
                !menuRefs.current[id].contains(event.target)
            ) {
                setOpenMenus((prevMenus) => ({
                ...prevMenus,
                [id]: false,
                }));
            }
            });
        },
        [openMenus]
    );

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [handleClickOutside]);

    const openModal = (topicId, topicTitle) => {
        setSelectedTopicId(topicId);
        setSelectedTopicTitle(topicTitle);
        setIsModalOpen(true); // Open the modal
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedTopicId(null); // Clear the topic ID when closing
        setSelectedTopicTitle(null); // Clear the title as well
    };

    const fetchTopics = useCallback(async () => {
    try {
        const response = await api.get(`/api/sessions/${id}/topics`);
        setTopics(response.data.topics);
        setPresentedTopicId(response.data.presentedTopicId);
        setPresentedAmendmentId(response.data.presentedAmendmentId ?? null);
        setAmendmentIsPresented(!!response.data.presentedAmendmentId);
        setTopicsLoaded(true);
        return response.data.topics; // return the topics
    } catch (error) {
        console.error("Error fetching topics:", error);
        return []; // return empty array on error
    }
}, [id]);

    useEffect(() => {
        const cachedSessions = localStorage.getItem(`sessions_${municipalityId}`);
        if (cachedSessions) {
            const sessions = JSON.parse(cachedSessions);
            const session = sessions.find(s => s.id === parseInt(id));
            if (session) {
                setSessionTitle(session.name);
                setSessionMunicipalityTermId(session.municipalityMandateId);
            }
        }
    }, [municipalityId, id]);

    const fetchUserVotes = useCallback(async () => {
        // Only fetch votes for ROLE_USER or ROLE_PRESIDENT
        if (!['ROLE_USER', 'ROLE_PRESIDENT'].includes(userInfo.role)) return;

        try {
            const response = await api.get(`/api/sessions/${id}/topics-user-votes`);

            const votesMap = {};
            response.data.forEach(([topicId, voteStatus]) => {
                votesMap[topicId] = voteStatus;
            });

            setCurrentVotes(votesMap);
        } catch (error) {
            console.error("Error fetching user votes:", error);
        }
    }, [id, userInfo.role]);

    const fetchOnlineUsers = useCallback(async () => {
        if (!municipalityId) return;

        try {
            const response = await api.get(
            `/api/municipalities/${municipalityId}/online-users`
            );

            setOnlineUsersNumber(response.data);
        } catch (error) {
            console.error("Error fetching online users:", error);
        }
    }, [municipalityId]);

    useEffect(() => {
        if (showFixDiv) {
            fetchOnlineUsers();
        }
    }, [fetchOnlineUsers, showFixDiv]);

    // Populate image cache once per mandate term
    useEffect(() => {
        if (!sessionMunicipalityTermId) return;
        if (isTermPopulated(sessionMunicipalityTermId)) return;
        api.get(`/api/municipality-terms/${sessionMunicipalityTermId}/user-images`)
            .then(res => storeTermImages(sessionMunicipalityTermId, res.data))
            .catch(() => {});
    }, [sessionMunicipalityTermId]);

    useEffect(() => {
        fetchTopics();
        if (
        showFixDiv &&
        (userInfo.role === "ROLE_USER" || userInfo.role === "ROLE_PRESIDENT")
        ) {
            fetchUserVotes();
        }
    }, [token, userInfo, fetchTopics, id, fetchUserVotes, showFixDiv]);

    const handleDelete = async () => {
        try {
            await api.delete(`/api/topics/${selectedTopicId}`);

            // Close modal and refresh topics
            closeModal();
            await fetchTopics();

            // Send WebSocket update
            sendNewTopic("NEW_TOPIC");
        } catch (error) {
            console.error("Error deleting the topic:", error);
        }
    };

    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            const element = document.getElementById(hash.substring(1)); // Remove the '#' from the hash
            if (element) {
                // Timeout to allow the DOM to finish rendering
                const timeoutId = setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                    // Clear the hash from the URL after scrolling
                    window.history.replaceState(null, null, ' ');
                },1); 

                return () => clearTimeout(timeoutId); 
            }
        }
    }, [topics]);

  

    const handlePdfFetch = async (pdfId) => {
        const newTab = openPdfTab();
        try {
            const { data } = await api.get(`/api/topics/pdf/${pdfId}/name`);
            const token = localStorage.getItem('jwtToken');
            const baseUrl = process.env.REACT_APP_API_URL || '';
            const safeName = sanitizeFilenameForUrl(data.fileName);
            if (newTab && !newTab.closed) {
                newTab.location.href = `${baseUrl}/api/topics/pdf/${pdfId}/${encodeURIComponent(safeName)}?token=${encodeURIComponent(token)}`;
            }
        } catch (error) {
            console.error('Error fetching PDF:', error);
            if (newTab && !newTab.closed) newTab.close();
        }
    };

    const handleStartVotingClick = (topicId) => {
        const activeTopic = topics.find(t => t.topicStatus === 'ACTIVE');
        if (activeTopic) {
            setActiveWarningTopicTitle(activeTopic.title);
            setActiveWarningTopicId(activeTopic.id);
            setPendingVotingTopicId(topicId);
            setIsActiveWarningModalOpen(true);
        } else {
            startVoting(topicId);
        }
    };

    const handleActiveWarningConfirm = async () => {
        setIsActiveWarningModalOpen(false);
        if (activeWarningTopicId) await finishVoting(activeWarningTopicId);
        if (pendingVotingTopicId) await startVoting(pendingVotingTopicId);
        setActiveWarningTopicId(null);
        setPendingVotingTopicId(null);
    };

    const startVoting = async (topicId) => {
        try {
            await api.get(`/api/sessions/${id}/topics/active/${topicId}`);

            // Update local state
            setIsVoteAction(true);
            console.log('Voting started successfully');

            // Refresh topics
            await fetchTopics();

            // Update current votes state
          if (userInfo.role === "ROLE_ADMIN") {
    console.log('Admin detected — skipping currentVotes update');
} else {
    setCurrentVotes((prevVotes) => ({
        ...prevVotes,
        [String(topicId)]: 'HAVE_NOT_VOTED',
    }));
}
            // Send WebSocket updates
            sendVote(topicId);
            sendPresenterUpdate(topicId);
        } catch (error) {
            console.error('Error starting voting:', error);
        }
    };

    const finishVoting = async (topicId) => {
        try {
            await api.get(`/api/sessions/${id}/topics/finish/${topicId}`);

            // Update local state
            setIsVoteAction(true);

            // Refresh topics
            await fetchTopics();

            console.log('Voting finished successfully');

            // Send WebSocket updates
            sendVote(topicId);
            sendPresenterUpdate(topicId);
        } catch (error) {
            console.error('Error finishing voting:', error);
        }
    };

    const restartVoting = async (topicId) => {
        try {
            // Axios GET request using your api instance
            await api.get(`/api/topics/create/${topicId}`);

            // Update local state
            setIsVoteAction(true);

            // Only update currentVotes for ROLE_USER and ROLE_PRESIDENT
            if (userInfo.role === 'ROLE_USER' || userInfo.role === 'ROLE_PRESIDENT') {
                setCurrentVotes((prevVotes) => {
                    const updatedVotes = { ...prevVotes };
                    delete updatedVotes[topicId]; // Remove votes for this topic
                    return updatedVotes;
                });
            }

            console.log('Voting restarted successfully');

            // Refresh topics
            await fetchTopics();

            // Send WebSocket updates
            sendVote(topicId);
            sendPresenterUpdate(topicId);
        } catch (error) {
            console.error('Error restarting voting:', error);
        }
    };

    const handleVote = async (topicId, voteType) => {

        // Prevent sending request if vote is already the same
        if (currentVotes[topicId] === voteType) {
            return;
        }

        // Prevent concurrent votes for the same topic
        if (votingInProgressRef.current.has(topicId)) {
            return;
        }

        votingInProgressRef.current.add(topicId);

        const prevVoteType = currentVotes[topicId];

        try {
            await api.post(`/api/topics/vote/${topicId}/${voteType}`);

            // Mark this as a vote action
            setIsVoteAction(true);

            // Update local vote state immediately (optimistic UI)
            setCurrentVotes((prevVotes) => ({
                ...prevVotes,
                [topicId]: voteType,
            }));

            // Optimistically update vote counts on the buttons
            const voteFieldMap = {
                'YES': 'yes',
                'NO': 'no',
                'ABSTAINED': 'abstained',
                'CANNOT_VOTE': 'cantVote',
                'HAVE_NOT_VOTED': 'haveNotVoted',
            };
            setTopics((prevTopics) =>
                prevTopics.map((topic) => {
                    if (topic.id !== topicId) return topic;
                    const updated = { ...topic };
                    const newField = voteFieldMap[voteType];
                    const prevField = voteFieldMap[prevVoteType];
                    if (newField) updated[newField] = (updated[newField] || 0) + 1;
                    if (prevField) updated[prevField] = Math.max(0, (updated[prevField] || 0) - 1);
                    return updated;
                })
            );

            // Notify others via WebSocket
            sendVote(topicId, voteType, userInfo.username);

        } catch (error) {
            console.error("Error submitting vote:", error);
        } finally {
            votingInProgressRef.current.delete(topicId);
        }
    };

    useEffect(() => {
        // Reveal the floating back button once the in-header one has scrolled away
        const handleScroll = () => {
            setShowFloatingBack(window.scrollY > 200);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        // Save scroll position on refresh
        const handleBeforeUnload = () => {
            sessionStorage.setItem('scrollPosition', window.scrollY);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const saveScrollPosition = () => {
        const scrollPosition = window.scrollY;
        sessionStorage.setItem('scrollPosition', scrollPosition);
    };

    useEffect(() => {
        if (!['ROLE_USER', 'ROLE_PRESIDENT'].includes(userInfo.role)) return;
        isVoteActionRef.current = isVoteAction;
    }, [isVoteAction, userInfo.role]);

    useEffect(() => {
        if (!topicsLoaded) return; // Wait until first load is complete

        // Skip if action was from logo or from a voting WebSocket update
        if (isVoteActionRef.current) {
            isVoteActionRef.current = false;
            return;
        }

        const scrollPosition = sessionStorage.getItem('scrollPosition');
        if (scrollPosition) {
            const timeoutId = setTimeout(() => {
            window.scrollTo({ top: parseInt(scrollPosition, 10), behavior: 'smooth' });
            }, 200); // small delay to ensure layout is rendered

            return () => clearTimeout(timeoutId);
        }
    }, [topicsLoaded]);

    const currentSession = (JSON.parse(localStorage.getItem(`sessions_${municipalityId}`)) || [])
        .find(s => s.id === parseInt(id));

    const canVote = (
        (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') &&
        userInfo.status === "ACTIVE" &&
        Number(municipalityId) === Number(userInfo.municipalityId) &&
        Array.isArray(userInfo.municipalityTermIds) &&
        currentSession &&
        userInfo.municipalityTermIds.includes(Number(currentSession.municipalityMandateId))
    );

    const canParticipateInSpeaking = (
        (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER' || userInfo.role === 'ROLE_MAYOR') &&
        userInfo.status === "ACTIVE" &&
        Number(municipalityId) === Number(userInfo.municipalityId) &&
        Array.isArray(userInfo.municipalityTermIds) &&
        currentSession &&
        userInfo.municipalityTermIds.includes(Number(currentSession.municipalityMandateId))
    );

    useEffect(() => {
        if (!presenterMessages.length) return;
        const lastTopicId = Number(presenterMessages[presenterMessages.length - 1]);
        setPresentedTopicId(lastTopicId);
        setPresentedAmendmentId(null);
        setAmendmentIsPresented(false);
    }, [presenterMessages]);

    useEffect(() => {
        if (!amendmentPresenterMessages.length) return;
        const lastAmendmentId = Number(amendmentPresenterMessages[amendmentPresenterMessages.length - 1]);
        setPresentedAmendmentId(lastAmendmentId || null);
        setAmendmentIsPresented(true);
    }, [amendmentPresenterMessages]);

useEffect(() => {
  if (newTopicMessages.length > 0) {
    (async () => {
      const updatedTopics = await fetchTopics(); // now it returns topics

      if (canVote) {
        setCurrentVotes(prevVotes => {
          const newVotes = { ...prevVotes };
          updatedTopics.forEach(topic => {
            if (!(topic.id in newVotes)) {
              newVotes[topic.id] = "HAVE_NOT_VOTED";
            }
          });
          localStorage.setItem(`currentVotes_session_${id}`, JSON.stringify(newVotes));
          return newVotes;
        });
      }
    })();
  }
}, [newTopicMessages, fetchTopics, id, canVote]);




    const handlePresentClick = async (topicId) => {
        try {
            // Using Axios GET
            await api.get(`/api/topics/present/${topicId}`);

            // Send WebSocket update
            sendPresenterUpdate(topicId);
        } catch (error) {
            console.error("Failed to present topic:", error);
        }
    };

    useEffect(() => {
        if (voteMessages.length === 0) return;

        const lastResult = voteMessages.at(-1);
        const updatedTopicId = lastResult.topicId;

        // Update topic vote counts and status
        setTopics((prevTopics) =>
            prevTopics.map((topic) =>
                topic.id === updatedTopicId
                    ? {
                        ...topic,
                        yes: lastResult.yes,
                        no: lastResult.no,
                        abstained: lastResult.abstained,
                        cantVote: lastResult.cantVote,
                        haveNotVoted: lastResult.haveNotVoted,
                        absent: lastResult.absent,
                        topicStatus: lastResult.status,
                    }
                    : topic
            )
        );

        // Sync currentVotes for the same user logged in on another device
        if (canVote && lastResult.voterUsername === userInfo.username && lastResult.voteType) {
            setCurrentVotes((prevVotes) => ({
                ...prevVotes,
                [updatedTopicId]: lastResult.voteType,
            }));
        }

        // Reset currentVotes if topic is restarted
        if (canVote && lastResult.status === 'CREATED') {
            setCurrentVotes((prevVotes) => ({
                ...prevVotes,
                [updatedTopicId]: 'HAVE_NOT_VOTED',
            }));
        }
    }, [voteMessages, canVote, userInfo.username]);



    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const isSessionLocked = userInfo.role !== 'ROLE_ADMIN' &&
        currentSession && new Date(currentSession.date) < twoMonthsAgo;

    const now = new Date();
    const sessionDate = currentSession ? new Date(currentSession.date) : null;
    const isSpeakingLocked = userInfo.role !== 'ROLE_ADMIN' &&
        sessionDate &&
        (sessionDate.getMonth() !== now.getMonth() || sessionDate.getFullYear() !== now.getFullYear());

    const hasTopicPermissionsPlusUser = (
    (
        (['ROLE_PRESIDENT', 'ROLE_EDITOR', 'ROLE_USER'].includes(userInfo.role)) &&
        userInfo.status === "ACTIVE" &&
        Number(municipalityId) === Number(userInfo.municipalityId) &&
        Array.isArray(userInfo.municipalityTermIds) &&
        currentSession &&
        userInfo.municipalityTermIds.includes(Number(currentSession.municipalityMandateId))
    ) 
    || userInfo.role === 'ROLE_ADMIN'
);

    const hasTopicPermissions  = (
        ((userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_EDITOR') &&
        userInfo.status === "ACTIVE" &&
        Number(municipalityId) === Number(userInfo.municipalityId) &&
        Array.isArray(userInfo.municipalityTermIds) &&
        currentSession &&
        userInfo.municipalityTermIds.includes(Number(currentSession.municipalityMandateId))
        ) || userInfo.role === 'ROLE_ADMIN'
    );

    const hasTopicPermissionsStatus  = (
        (userInfo.role === 'ROLE_PRESIDENT' &&
        userInfo.status === "ACTIVE" &&
        Number(municipalityId) === Number(userInfo.municipalityId) &&
        Array.isArray(userInfo.municipalityTermIds) &&
        currentSession &&
        userInfo.municipalityTermIds.includes(Number(currentSession.municipalityMandateId))
        ) || userInfo.role === 'ROLE_ADMIN'
    );

    function calculateProgress(topics) {
        if (topics.length === 0) return 0; // Avoid division by zero

        // Count FINISHED, INFORMATION, and WITHDRAWN topics
        const finishedCount = topics.filter(
            (topic) =>
                topic.topicStatus === "FINISHED" ||
                topic.topicStatus === "INFORMATION" ||
                topic.topicStatus === "WITHDRAWN"
        ).length;

        // Calculate percentage based on all topics
        return Math.min((finishedCount / topics.length) * 100, 100);
    }

    return (
        <div className="topics-container">
            <HelmetProvider>
                <Helmet>
                    <title>{t("topicsPage.pageTitle")}</title>
                </Helmet>
            </HelmetProvider>
            <Header />
            <button
                className={`back-button back-button--floating ${showFloatingBack ? 'is-visible' : ''}`}
                onClick={() => navigate(`/municipalities/${municipalityId}/sessions#session-${id}`)}
                aria-hidden={!showFloatingBack}
                tabIndex={showFloatingBack ? 0 : -1}
            >
                <span className="back-icon">
                    <FontAwesomeIcon icon={faChevronLeft} />
                </span>
                <span className="back-text">
                    {t("common.back")}
                </span>
            </button>
            <main className="topcis-container-body">
                <div className='d-flex justify-content-center'>
                     <div className="topic-header">
                        <div className='topic-header-title-div'>
                            <button
                                className="back-button"
                                onClick={() => navigate(`/municipalities/${municipalityId}/sessions#session-${id}`)}
                            >
                                <span className="back-icon">
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                </span>
                                <span className="back-text">
                                    {t("common.back")}
                                </span>
                            </button>
                            <h1 className="topic-header-title">{t("topicsPage.headerTitle")}</h1>

                        {["ROLE_ADMIN", "ROLE_PRESIDENT", "ROLE_USER", "ROLE_MAYOR", "ROLE_EDITOR"].includes(userInfo.role) && topics.length > 0 && (
                            <div className="progress-bar-container">
                                <div
                                    className="progress-bar-fill"
                                    style={{ width: `${calculateProgress(topics)}%` }}
                                ></div>
                                <span className="progress-text">{Math.round(calculateProgress(topics))}%</span>
                            </div>
                        )}

                        <div>
                            {sessionTitle && <h6 className='session-title'>{sessionTitle}</h6>}
                        </div>
                        </div>
                        <div className="session-button-container">
                             <Link to={`/municipalities/${municipalityId}/sessions/${id}/topics/add-form`}>
                                {hasTopicPermissions && !isSessionLocked && (
                                    <button className="entity-add-button" onClick={saveScrollPosition}>
                                        {t("common.add")} <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                )}
                            </Link>
                        </div>
                    </div>
                </div>
                

                <div className="topic-body">
                    {(isOn
                        ? topics.filter(topic => topic.id === presentedTopicId) // show only presented topic when toggle is on
                        : topics
                    )
                    .sort((a, b) => a.order_id - b.order_id)
                    .map(topic => (
                        <div key={topic.id} className='topic-div-rel'>
                           {topic.id === presentedTopicId && !amendmentIsPresented && userInfo?.role !== "ROLE_GUEST" && !isOn && (
                                <FontAwesomeIcon 
                                    icon={faBookmark} 
                                    className="topic-bookmark-icon" 
                                    title={t("topicsPage.presentBadge")} 
                                />
                            )}
                            <span id={`topic-${topic.id}`} className="topic-span-id"></span>
                          <div className={`topic-item 
                            ${(topic.topicStatus === 'FINISHED' || topic.topicStatus === 'WITHDRAWN' || topic.topicStatus === 'INFORMATION') ? 'finished-topic' : ''} 
                            topic-item-size`}>
                                <div className="topic-header-div">
                                   <h3 className="text-center">
                                        {topic.pdfFileId ? (
                                            <span
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handlePdfFetch(topic.pdfFileId);
                                                }}
                                                className={`topic-header-text topic-header-pdf
                                                    ${
                                                        ["ROLE_ADMIN", "ROLE_EDITOR", "ROLE_PRESIDENT", "ROLE_USER"].includes(userInfo?.role)
                                                            ? "ape-width"
                                                            : ""
                                                    }
                                                    ${
                                                        ["ROLE_MAYOR", "ROLE_SPECTATOR"].includes(userInfo?.role)
                                                            ? "user-width"
                                                            : ""
                                                    }
                                                    ${
                                                        userInfo?.role === "ROLE_GUEST"
                                                            ? "guest-width"
                                                            : ""
                                                    }
                                                `}
                                            >
                                                {topic.title}
                                            </span>
                                        ) : (
                                            <span
                                                className={`topic-header-text
                                                    ${
                                                        ["ROLE_ADMIN", "ROLE_EDITOR", "ROLE_PRESIDENT", "ROLE_USER",].includes(userInfo?.role)
                                                            ? "ape-width"
                                                            : ""
                                                    }
                                                    ${
                                                        ["ROLE_MAYOR", "ROLE_SPECTATOR"].includes(userInfo?.role)
                                                            ? "user-width"
                                                            : ""
                                                    }
                                                    ${
                                                        userInfo?.role === "ROLE_GUEST"
                                                            ? "guset-width"
                                                            : ""
                                                    }
                                                `}
                                            >
                                                {topic.title}
                                            </span>
                                        )}
                                    </h3>
                                    <div className='menu-wrapper'>
                                        {hasTopicPermissionsPlusUser && (
                                            <div className="menu-container" ref={(el) => (menuRefs.current[topic.id] = el)}>
                                            <div
                                                    className={`menu-dots ${openMenus[topic.id] ? 'open' : ''}`}
                                                    onClick={() => toggleMenu(topic.id)}
                                                    >
                                                    <FontAwesomeIcon className="menu-dots-icon" icon={faEllipsisV} />
                                                </div>
                                                {openMenus[topic.id] && (
                                                    <ul className="menu-list">
                                                        {['ROLE_USER', 'ROLE_ADMIN', 'ROLE_PRESIDENT'].includes(userInfo.role) && (
                                                            <li>
                                                                <Link
                                                                    to={`/municipalities/${municipalityId}/sessions/${id}/topics/amendments/${topic.id}`}
                                                                    onClick={saveScrollPosition}
                                                                >
                                                                    <span>
                                                                        {t("topicsPage.amendments")}{" "}
                                                                        <FontAwesomeIcon icon={faFilePen} />
                                                                    </span>
                                                                </Link>
                                                            </li>
                                                        )}
                                                    {(userInfo.role === 'ROLE_ADMIN' || userInfo.role === 'ROLE_PRESIDENT') && (
                                                            <li>
                                                                <span
                                                                    onClick={() => {
                                                                        handlePresentClick(topic.id);
                                                                        toggleMenu(topic.id);
                                                                    }}
                                                                >
                                                                    {t("topicsPage.present")} <FontAwesomeIcon icon={faDesktop} />
                                                                </span>
                                                            </li>
                                                        )}
                                                    {['ROLE_EDITOR', 'ROLE_ADMIN', 'ROLE_PRESIDENT'].includes(userInfo.role) && !isSessionLocked && (
                                                            <>
                                                                <li>
                                                                    <Link
                                                                        to={`/municipalities/${municipalityId}/sessions/${id}/topics/add-before/${topic.id}`}
                                                                        onClick={saveScrollPosition}
                                                                    >
                                                                        <span>
                                                                            {t("topicsPage.newTopicBefore")}{" "}
                                                                            <FontAwesomeIcon icon={faArrowUp} />
                                                                        </span>
                                                                    </Link>
                                                                </li>

                                                                <li>
                                                                    <Link
                                                                        to={`/municipalities/${municipalityId}/sessions/${id}/topics/add-after/${topic.id}`}
                                                                        onClick={saveScrollPosition}
                                                                    >
                                                                        <span>
                                                                            {t("topicsPage.newTopicAfter")}{" "}
                                                                            <FontAwesomeIcon icon={faArrowDown} />
                                                                        </span>
                                                                    </Link>
                                                                </li>

                                                                <li>
                                                                    <Link
                                                                        to={`/municipalities/${municipalityId}/sessions/${id}/topics/edit/${topic.id}`}
                                                                        onClick={saveScrollPosition}
                                                                    >
                                                                        {t("common.edit")}{" "}
                                                                        <FontAwesomeIcon icon={faPenToSquare} />
                                                                    </Link>
                                                                </li>

                                                                <li className="topic-delete-button">
                                                                    <span
                                                                        onClick={() => {
                                                                            openModal(topic.id, topic.title);
                                                                            toggleMenu(topic.id);
                                                                        }}
                                                                    >
                                                                        {t("topicsPage.delete")}{" "}
                                                                        <FontAwesomeIcon icon={faTrash} />
                                                                    </span>
                                                                </li>
                                                            </>
                                                        )}
                                                    </ul>
                                                )}
                                            </div>
                                        )}
                                    </div>                    
                                </div>

                                {(topic.amount || topic.hasAmendments || topic.hasCommissions) && (
                                    <div className={`topic-pill-container ${
                                        [topic.hasCommissions, !!topic.amount, topic.hasAmendments].filter(Boolean).length > 1
                                            ? 'topic-pill-container--multi'
                                            : ''
                                    }`}>
                                        {topic.hasCommissions && (
                                            <Link
                                                to={`/municipalities/${municipalityId}/sessions/${id}/topics/commissions/${topic.id}`}
                                                onClick={saveScrollPosition}
                                                className="topic-commissions-button-link"
                                            >
                                                <div className="topic-commissions-button">
                                                    <FontAwesomeIcon icon={faLandmarkFlag} />
                                                    {t("topicsPage.commissions")}
                                                </div>
                                            </Link>
                                        )}
                                        {topic.amount && (
                                            <div className="topic-amount-container">
                                                {topic.amount} {t("topicsPage.currency")}
                                            </div>
                                        )}
                                        {topic.hasAmendments && (
                                            <Link
                                                to={`/municipalities/${municipalityId}/sessions/${id}/topics/amendments/${topic.id}`}
                                                onClick={saveScrollPosition}
                                                className="amendments-button-link"
                                            >
                                                <div className="amendments-button">
                                                    <FontAwesomeIcon icon={faFilePen} />
                                                    {t("topicsPage.amendments")}
                                                </div>
                                            </Link>
                                        )}
                                    </div>
                                )}

                                <div className='topic-item-body'>
                                    {(topic.topicStatus === "ACTIVE" || topic.topicStatus === "FINISHED") && (
                                   <div
                                        className={`topic-item-body-detail ${
                                            topic.topicStatus === 'ACTIVE'
                                            ? 'topic-item-body-detail-active'
                                            : topic.topicStatus === 'FINISHED'
                                            ? 'topic-item-body-detail-finish'
                                            : ''
                                        }`}
                                        >
                                        <div className="topic-item-body-detail-group">
                                           <div className="topic-item-body-detail-group-chunk">
                                                    <div className="rez-container">
                                                        <span className="text-for-rez">{t("topicsPage.yes")}</span>
                                                    </div>
                                                    <div
                                                        onClick={canVote && topic.topicStatus === 'ACTIVE' ? () => handleVote(topic.id, 'YES') : undefined}
                                                        className={[
                                                            'topic-button-vote',
                                                            'vote-yes',
                                                            currentVotes[topic.id] === 'YES' && canVote ? 'active-vote' : '',
                                                            topic.topicStatus === 'ACTIVE' && currentVotes[topic.id] !== 'YES' &&
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-scale' : '',
                                                            topic.topicStatus === 'ACTIVE' &&
                                                            canVote ? 'vote-activated' : '',
                                                            topic.topicStatus === 'FINISHED' ? 'vote-yes-finished' : '',
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-hover-enabled' : ''
                                                        ].join(' ')}
                                                    >
                                                        {topic.yes}
                                                    </div>
                                            </div>
                                           <div className="topic-item-body-detail-group-chunk">
                                                    <div className="rez-container">
                                                        <span className="text-for-rez">{t("topicsPage.no")}</span>
                                                    </div>
                                                    <div
                                                        onClick={canVote && topic.topicStatus === 'ACTIVE' ? () => handleVote(topic.id, 'NO') : undefined}
                                                        className={[
                                                            'topic-button-vote',
                                                            'vote-no',
                                                            currentVotes[topic.id] === 'NO' && canVote ? 'active-vote' : '',
                                                            topic.topicStatus === 'ACTIVE' && currentVotes[topic.id] !== 'NO' &&
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-scale' : '',
                                                            topic.topicStatus === 'ACTIVE' &&
                                                            canVote ? 'vote-activated' : '',
                                                            topic.topicStatus === 'FINISHED' ? 'vote-no-finished' : '',
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-hover-enabled' : ''
                                                        ].join(' ')}
                                                        >
                                                        {topic.no}
                                                    </div>
                                            </div>
                                        </div>
                                        <div className="topic-item-body-detail-group">
                                           <div className="topic-item-body-detail-group-chunk">
                                                 <div className="rez-container">
                                                     <span className="text-for-rez">{t("topicsPage.abstained")}</span>
                                                </div>
                                                <div
                                                    onClick={canVote && topic.topicStatus === 'ACTIVE' ? () => handleVote(topic.id, 'ABSTAINED') : undefined}
                                                   className={[
                                                        'topic-button-vote',
                                                        'vote-abstained',
                                                        currentVotes[topic.id] === 'ABSTAINED' && canVote ? 'active-vote' : '',
                                                        topic.topicStatus === 'ACTIVE' && currentVotes[topic.id] !== 'ABSTAINED' &&
                                                        (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-scale' : '',
                                                        topic.topicStatus === 'ACTIVE' &&
                                                        canVote ? 'vote-activated' : '',
                                                        topic.topicStatus === 'FINISHED' ? 'vote-abstained-finished' : '',
                                                        (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-hover-enabled' : ''
                                                    ].join(' ')}
                                                    >
                                                    {topic.abstained}
                                                </div>
                                            </div>
                                            <div className="topic-item-body-detail-group-chunk">
                                                    <div className="rez-container">
                                                        <span className="text-for-rez">{t("topicsPage.cantVote")}</span>
                                                    </div>
                                                    <div
                                                        onClick={canVote && topic.topicStatus === 'ACTIVE' ? () => handleVote(topic.id, 'CANNOT_VOTE') : undefined}
                                                        className={[
                                                            'topic-button-vote',
                                                            'vote-cantvote',
                                                            currentVotes[topic.id] === 'CANNOT_VOTE' && canVote ? 'active-vote' : '',
                                                            topic.topicStatus === 'ACTIVE' && currentVotes[topic.id] !== 'CANNOT_VOTE' &&
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-scale' : '',
                                                            topic.topicStatus === 'ACTIVE' &&
                                                            canVote ? 'vote-activated' : '',
                                                            topic.topicStatus === 'FINISHED' ? 'vote-cantvote-finished' : '',
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-hover-enabled' : ''
                                                        ].join(' ')}
                                                        >
                                                        {topic.cantVote}
                                                    </div>
                                            </div>
                                        </div>
                                        <div className="topic-item-body-detail-group">
                                           <div className="topic-item-body-detail-group-chunk">
                                                    <div className="rez-container">
                                                       <span className="text-for-rez">{t("topicsPage.notVoted")}</span>
                                                    </div>
                                                    <div
                                                        onClick={canVote && topic.topicStatus === 'ACTIVE' ? () => handleVote(topic.id, 'HAVE_NOT_VOTED') : undefined}
                                                        className={[
                                                            'topic-button-vote',
                                                            'vote-haventvote',
                                                            currentVotes[topic.id] === 'HAVE_NOT_VOTED' && canVote ? 'active-vote' : '',
                                                            topic.topicStatus === 'ACTIVE' && currentVotes[topic.id] !== 'HAVE_NOT_VOTED' &&
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-scale' : '',
                                                            topic.topicStatus === 'ACTIVE' &&
                                                            canVote ? 'vote-activated' : '',
                                                            topic.topicStatus === 'FINISHED' ? 'vote-haventvote-finished' : '',
                                                            (userInfo.role === 'ROLE_PRESIDENT' || userInfo.role === 'ROLE_USER') && userInfo.status === "ACTIVE" && userInfo.municipalityId === municipalityId ? 'vote-hover-enabled' : ''
                                                        ].join(' ')}
                                                        >
                                                        {topic.haveNotVoted}
                                                    </div>
                                                </div>
                                            <div className="topic-item-body-detail-group-chunk">
                                                <div className="rez-container">
                                                    <span className="text-for-rez">{t("topicsPage.absent")}</span>
                                                </div>
                                                <div className="topic-button-vote vote-absent">{topic.absent}</div>
                                            </div>
                                        </div>
                                </div>
                             )}

                        {(topic.topicStatus === "INFORMATION" || topic.topicStatus === "WITHDRAWN") && (
                            <div className="topic-status-badge-wrapper">
                                {topic.topicStatus === "INFORMATION" && (
                                    <span className="topic-list-status-badge topic-list-status-badge--information">{t("topicsPage.information")}</span>
                                )}
                                {topic.topicStatus === "WITHDRAWN" && (
                                    <span className="topic-list-status-badge topic-list-status-badge--withdrawn">{t("topicsPage.withdrawn")}</span>
                                )}
                            </div>
                        )}


                                        <div className="topic-item-body-detail-footer">
                                            <div className="topic-item-body-detail-group-footer">
                                              {topic.topicStatus !== 'WITHDRAWN' && topic.topicStatus !== 'INFORMATION' && (
                                                    <div className="command-buttons">
                                                       <Link
                                                            to={`/municipalities/${municipalityId}/sessions/${id}/topics/details/${topic.id}`}
                                                            className={`gold-button ${selectedLang}`}
                                                            onClick={saveScrollPosition}
                                                            >
                                                            {topic.topicStatus === "CREATED"
                                                            ? t("topicsPage.details")
                                                            : t("topicsPage.detailedResults")}&nbsp;
                                                        <FontAwesomeIcon icon={faSquarePollVertical} />
                                                        </Link>
                                                    </div>
                                                )}


                                            {hasTopicPermissionsStatus && !isSessionLocked && (
                                                    <div className="command-buttons-group">
                                                        {topic.topicStatus === "CREATED" && (
                                                            <div className="command-buttons">
                                                                <button
                                                                    onClick={() => handleStartVotingClick(topic.id)}
                                                                    className={`change-topic-status-button ${selectedLang}`}
                                                                >
                                                                    {t("topicsPage.startVoting")} <FontAwesomeIcon icon={faCirclePlay} />
                                                                </button>
                                                            </div>
                                                        )}
                                                        {topic.topicStatus === "ACTIVE" && (
                                                            <div className="command-buttons">
                                                                <button
                                                                    onClick={() => finishVoting(topic.id, token)}
                                                                    className={`change-topic-status-button ${selectedLang}`}
                                                                >
                                                                    {t("topicsPage.finishVoting")} <FontAwesomeIcon icon={faCircleStop} />
                                                                </button>
                                                            </div>
                                                        )}
                                                        {topic.topicStatus === "FINISHED" && (
                                                            <div className="command-buttons">
                                                                <button
                                                                    onClick={() => openRestartModal(topic.id, topic.title)}
                                                                    className={`change-topic-status-button ${selectedLang}`}
                                                                >
                                                                    {t("topicsPage.restartVoting")} <FontAwesomeIcon icon={faRotateLeft} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )) }
                    <div className="mt-4">
                        {topics.length > 2 && hasTopicPermissions && !isSessionLocked && !isOn && (
                            <Link to={`/municipalities/${municipalityId}/sessions/${id}/topics/add-form`}>
                                <button
                                    className="entity-add-button"
                                    onClick={saveScrollPosition}
                                >
                                    {t("common.add")} <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
            </main>
            {topics.length > 0 && <Footer />}


            {showFixDiv && !isSessionLocked && (
                <div
                className={`fixed-position-div ${showNumber ? 'show-number' : 'div-bigger'}`}
                onClick={toggleVisibility}
                >
                    <div className="arrow">
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </div>

                    <div className="fpdiv-content">
                        <div className="tooltip-container" onClick={(e) => e.stopPropagation()}>
                            <div onClick={handleToggle} className="toggle-topics">
                                <FontAwesomeIcon icon={isOn ? faToggleOn : faToggleOff} />
                            </div>
                            <span className="tooltip-text">{t("tooltip.easyMode")}</span>
                        </div>

                        <div onClick={(e) => e.stopPropagation()}>
                            <div className="number" onClick={() => setIsLiveModalOpen(true)}>
                                <p className="number-content">
                                    <span className="number-content-span">{onlineUsersNumber}</span>
                                    <FontAwesomeIcon icon={faUsers} />
                                </p>
                            </div>
                        </div>
                    </div>
            </div>
            )}

          {isModalOpen && (
                <TopicConfirmModal
                    isOpen={isModalOpen}  
                    onClose={closeModal}  
                    onConfirm={handleDelete}
                    topicTitle={selectedTopicTitle ? `${selectedTopicTitle}` : ''}
                />
            )}


           {isRestartModalOpen && (
                <RestartTopicStatusModal
                    isOpen={isRestartModalOpen}
                    onClose={closeRestartModal}
                    topicTitle={restartTopicTitle}
                    onConfirm={handleRestartConfirm}
                />
            )}

            {isActiveWarningModalOpen && (
                <ActiveTopicWarningModal
                    isOpen={isActiveWarningModalOpen}
                    onClose={() => {
                        setIsActiveWarningModalOpen(false);
                        setPendingVotingTopicId(null);
                        setActiveWarningTopicId(null);
                    }}
                    onConfirm={handleActiveWarningConfirm}
                    activeTopicTitle={activeWarningTopicTitle}
                />
            )}



       <LiveUsersModal
            isOpen={isLiveModalOpen}
            onClose={() => setIsLiveModalOpen(false)}
            municipalityId={municipalityId}
            canSeeAction={hasTopicPermissionsStatus}
        />

        <SpeakingPanel
            presentedTopicId={presentedTopicId}
            presentedAmendmentId={presentedAmendmentId}
            isPresentedFinished={
                !amendmentIsPresented &&
                topics.find(t => t.id === presentedTopicId)?.topicStatus === 'FINISHED'
            }
            userInfo={userInfo}
            isPresidentOrAdmin={hasTopicPermissionsStatus}
            canParticipate={canParticipateInSpeaking}
            isLocked={isSpeakingLocked}
            municipalityId={municipalityId}
            sessionId={id}
        />
        </div>
    );
}

export default Topics;
